import React, { useState, useEffect, useRef } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axios from 'axios';
import {
  Container,
  Button,
  Grid,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const IMAGE_BASE_URL = "https://kumuduorderapp.blob.core.windows.net/804-jjj/";

const QRFetchOnly = () => {
  const [qrData, setQrData] = useState('');
  const [scanning, setScanning] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const qrScannerRef = useRef(null);

  useEffect(() => {
    if (scanning) {
      const scanner = new Html5QrcodeScanner('qr-reader', { fps: 5, qrbox: { width: 250, height: 250 } });
      scanner.render(onScanSuccess, onScanFailure);
      qrScannerRef.current = scanner;
    }

    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.clear().catch(err => console.error('Scanner clear error:', err));
      }
    };
  }, [scanning]);

  const onScanSuccess = (decodedText) => {
    setQrData(decodedText);
    setScanning(false);
    fetchStockDetails(decodedText);
  };

  const onScanFailure = (error) => {
    console.warn('Scan error:', error);
  };

  const fetchStockDetails = async (qrCode) => {
    if (!qrCode.trim()) {
      setError("QR Code is empty.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/getStockByQRCode`,
        { qrcode: qrCode },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );
      setApiResponse(response.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch stock details.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Grid container spacing={3} justifyContent="center" alignItems="center">
        <Grid item xs={12} md={5} textAlign="center">
          <div id="qr-reader" style={{ width: '100%', maxWidth: 300, margin: '0 auto' }}></div>
          <Button variant="contained" onClick={() => setScanning(true)} sx={{ mt: 2 }}>Start Scan</Button>
          <Button variant="outlined" color="secondary" onClick={() => setScanning(false)} sx={{ mt: 2, ml: 2 }}>
            Stop Scan
          </Button>
        </Grid>

        <Grid item xs={12} md={1} textAlign="center">
          <Typography fontWeight="bold">OR</Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="Enter QR Code"
            variant="outlined"
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => fetchStockDetails(qrData)}
            sx={{ mt: 2 }}
          >
            Fetch Stock
          </Button>
        </Grid>
      </Grid>

      {apiResponse && (
        <Grid container justifyContent="center" mt={4} mb={8}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: 3, borderRadius: 3, p: 1, textAlign: "center" }}>
              <CardMedia
                component="img"
                image={`${IMAGE_BASE_URL}${apiResponse.tagno}.jpg`}
                alt="Stock Item"
                sx={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 2, cursor: "pointer" }}
                onClick={() => setOpenImageDialog(true)}
                onError={(e) => (e.target.style.display = "none")}
              />
              <CardContent>
                {apiResponse.itemtype_name && (
                  <Typography variant="subtitle1" fontWeight="bold">
                    {apiResponse.itemtype_name}
                  </Typography>
                )}
                {apiResponse.subcolor && (
                  <Typography variant="subtitle1" fontWeight="bold">
                    {apiResponse.subcolor}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  Tagno: {apiResponse.tagno}
                </Typography>
                {apiResponse.gross && (
                  <Typography variant="body2" fontWeight="bold">
                    Gross Wt: {parseFloat(apiResponse.gross).toFixed(3)}
                  </Typography>
                )}
                {apiResponse.netwt && (
                  <Typography variant="body2" fontWeight="bold">
                    Net Wt: {parseFloat(apiResponse.netwt).toFixed(3)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Modal for full-size image */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ position: 'relative', p: 0 }}>
          <IconButton
            onClick={() => setOpenImageDialog(false)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff', background: 'rgba(0,0,0,0.5)' }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={`${IMAGE_BASE_URL}${apiResponse?.tagno}.jpg`}
            alt="Full View"
            style={{
              width: '100%', height: 'auto', display: 'block', maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default QRFetchOnly;
